import { useMemo } from 'react';
import {
  SellerContactsRow,
  type SellerContactsRowLabels,
} from '~/components/common/SellerContactsRow';
import {
  SellerOrganizationPicker,
  type PickableSellerOrganization,
  type SellerOrganizationPickerLabels,
} from '~/components/common/SellerOrganizationPicker';
import {
  SellerListToggles,
  type SellerListTogglesLabels,
} from '~/components/common/SellerListToggles';
import type { SellerRecipients } from '~/components/common/useSellerRecipients';
import { SelectAllCheckbox } from '~/components/common/SelectAllCheckbox';

export type SelectSellerContactsStepLabels = {
  sellerLists: SellerListTogglesLabels;
  picker: SellerOrganizationPickerLabels;
  row: SellerContactsRowLabels;
  selectAll: string;
};

type SelectSellerContactsStepProps = {
  recipients: SellerRecipients;
  isSubmitting: boolean;
  labels: SelectSellerContactsStepLabels;
  testIdPrefix: string;
  excludeOrganizationIds?: string[];
};

type SelectAllSellersProps = {
  listedOrganizations: PickableSellerOrganization[];
  recipients: SellerRecipients;
  label: string;
  testId: string;
  disabled: boolean;
};

function SelectAllSellers({
  listedOrganizations,
  recipients,
  label,
  testId,
  disabled,
}: Readonly<SelectAllSellersProps>) {
  const selectedCount = listedOrganizations.filter(({ id }) =>
    recipients.isOrganizationSelected(id)
  ).length;
  const allSelected = selectedCount === listedOrganizations.length;
  const someSelected = selectedCount > 0 && !allSelected;

  const toggleAll = () => {
    if (allSelected) {
      recipients.deselectOrganizations(listedOrganizations.map(({ id }) => id));
    } else {
      recipients.selectOrganizations(listedOrganizations);
    }
  };

  return (
    <div className="px-4">
      <SelectAllCheckbox
        allSelected={allSelected}
        someSelected={someSelected}
        onToggle={toggleAll}
        label={label}
        disabled={disabled}
        testId={testId}
      />
    </div>
  );
}

export function SelectSellerContactsStep({
  recipients,
  isSubmitting,
  labels,
  testIdPrefix,
  excludeOrganizationIds,
}: Readonly<SelectSellerContactsStepProps>) {
  const selectedOrganizationIds = useMemo(
    () => [...recipients.selectedOrganizationIds],
    [recipients.selectedOrganizationIds]
  );

  const turnOnSellerList: SellerRecipients['turnOnSellerList'] = (
    sellerListId,
    selections
  ) => {
    const excludedIds = new Set(excludeOrganizationIds);
    const includedSelections = selections.filter(
      ({ organization }) => !excludedIds.has(organization.id)
    );
    if (includedSelections.length > 0) {
      recipients.turnOnSellerList(sellerListId, includedSelections);
    }
  };

  return (
    <div className="flex flex-col gap-3">
      <SellerListToggles
        recipients={{ ...recipients, turnOnSellerList }}
        labels={labels.sellerLists}
        testIdPrefix={testIdPrefix}
        disabled={isSubmitting}
      />
      <SellerOrganizationPicker
        testId={`${testIdPrefix}-sellers`}
        pinnedOrganizationIds={selectedOrganizationIds}
        excludeOrganizationIds={excludeOrganizationIds}
        labels={labels.picker}
        renderListHeader={(listedOrganizations) => (
          <SelectAllSellers
            listedOrganizations={listedOrganizations}
            recipients={recipients}
            label={labels.selectAll}
            testId={`${testIdPrefix}-select-all-sellers`}
            disabled={isSubmitting}
          />
        )}
        renderOrganization={(organization, countAddedContact) => (
          <SellerContactsRow
            organization={organization}
            recipients={recipients}
            labels={labels.row}
            testId={`${testIdPrefix}-seller-row-${organization.id}`}
            onContactAdded={countAddedContact}
            disabled={isSubmitting}
          />
        )}
      />
    </div>
  );
}
